// (c) 캔버스 바인딩 배지 오버레이 — 바인딩된 **노드**(우상단 🔗) + 값 소스가 있는 **표 셀**(우상단
// 종류별 글리프)에 배지를 띄워 정적 vs 데이터를 한눈에 구분하고, hover 시 (b)와 같은 한글 요약을
// 툴팁으로 보여준다.
//
// 좌표는 DragLayer와 동일하게 [data-composer-node-id]/[data-composer-cell] 엘리먼트의
// getBoundingClientRect를 캔버스 기준 상대좌표로 환산해 구한다.
//  - 노드 배지: ComposerNode.binding(셀 안 삽입 노드 포함 — walkNodeTree 전 범위 하강).
//  - 셀 배지: TableCell.binding/compute/aggregate(값 소스가 실제로 표시되는 셀만 — rc effectiveCellSourceKind).
//    반복 전개 복제 셀은 같은 정체성 좌표 DOM이 여럿이므로 **모든** DOM 요소에 배지를 그린다
//    (makeUniqKey #N key로 React key 중복 방지).
//
// 성능: React.memo(props 불변 시 재렌더 skip) + 트리 수집(collectBoundNodes/collectBoundCells)은
// useMemo(body)로 캐시. DOM 순회/측정은 **렌더 단계가 아니라** useLayoutEffect(커밋 후) + 시그니처
// 비교 setState로 수행한다(PaddingOverlay와 동일 패턴 — 렌더 중 getBoundingClientRect 잔상/부하 제거).
// 결정 기록: docs/decisions/2026-06-22-composer-binding-visibility-korean-ux.md

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ComposerNode, CellSourceKind } from '@safetyhub/report-composer';
import type { Binding, CatalogEntry } from '@safetyhub/shared';
import { buildBindingSummary } from './bindingSummary';
import { getNodeRect, makeUniqKey, rectRelativeTo, type Rect } from './canvasRect';
import { walkNodeTree } from './nodeTreeWalk';
import { buildCellDefTooltip, cellCoordKey, collectBoundCells, type BoundCell } from './cellDefSummary';

/**
 * 바인딩 있는 노드(id+binding) 평탄화.
 * 하강 정책: group/stack.children · table cell.content · repeat.template 전부(walkNodeTree 기본값) —
 * 셀 안(insertNodeIntoCell로 삽입)에 바인딩된 노드도 배지가 뜨게 한다(walkNodeTree 전 범위 하강).
 */
function collectBoundNodes(body: ComposerNode[]): { id: string; binding: Binding }[] {
  const out: { id: string; binding: Binding }[] = [];
  walkNodeTree(body, (n) => {
    if (n.binding) out.push({ id: n.id, binding: n.binding });
  });
  return out;
}

/** 셀 배지 종류별 글리프/색 — 노드 🔗(파랑)와 톤을 맞추되 종류를 구분한다. */
const CELL_BADGE_STYLE: Record<CellSourceKind, { glyph: string; bg: string }> = {
  binding: { glyph: '🔗', bg: '#2563eb' }, // 값 바인딩 = 파랑(노드와 동일)
  compute: { glyph: 'ƒ', bg: '#7c3aed' }, // 계산 = 보라
  aggregate: { glyph: '∑', bg: '#059669' }, // 집계 = 초록
};

/** 그릴 배지 1개(측정 완료). */
interface BadgeSpec {
  key: string;
  title: string;
  glyph: string;
  bg: string;
  size: number;
  rect: Rect;
}

/** 배지 공통 시각(원형 + 반투명 hover 불투명). size는 노드=16 / 셀=14(과밀 완화). */
function badgeStyle(rect: Rect, bg: string, size: number): React.CSSProperties {
  return {
    position: 'absolute',
    top: rect.top - 6,
    left: rect.left + rect.width - (size - 6),
    width: size,
    height: size,
    borderRadius: '50%',
    background: bg,
    color: '#fff',
    fontSize: size === 16 ? 9 : 8,
    lineHeight: `${size}px`,
    textAlign: 'center',
    boxShadow: '0 0 0 1px #fff',
    pointerEvents: 'auto',
    cursor: 'help',
    zIndex: 24,
  };
}

/**
 * 바인딩 배지 오버레이 — 캔버스 div 내부(RenderTemplate 뒤, position:absolute)에 둔다.
 * 배지는 노드/셀 우상단에 표시되며 pointer-events는 배지에만 활성(노드 클릭/선택 방해 X).
 */
function BindingBadgeOverlayImpl({
  canvas,
  body,
  catalog,
}: {
  canvas: HTMLElement | null;
  body: ComposerNode[];
  catalog: CatalogEntry[];
}) {
  // 트리 수집은 body 변경 시에만(선택/줌 등 무관 재렌더에서 재수집 skip).
  const boundNodes = useMemo(() => collectBoundNodes(body), [body]);
  // 값 소스가 있는 셀 정의(패턴 셀 1개)를 정체성 좌표로 색인 → DOM 순회 시 O(1) 조회.
  const cellDefByCoord = useMemo(() => {
    const map = new Map<string, BoundCell>();
    for (const def of collectBoundCells(body)) {
      map.set(cellCoordKey(def.tableId, def.row, def.col), def);
    }
    return map;
  }, [body]);

  const sigRef = useRef<string>('');
  const [badges, setBadges] = useState<BadgeSpec[]>([]);

  // ⚠️ 측정은 커밋 후(useLayoutEffect), deps 없이 매 커밋마다 수행하고 시그니처가 바뀔 때만 setState.
  // (PaddingOverlay와 동일 — 렌더 중 querySelectorAll/getBoundingClientRect 잔상·부하 회피.)
  useLayoutEffect(() => {
    if (!canvas) {
      if (sigRef.current !== '') {
        sigRef.current = '';
        setBadges([]);
      }
      return;
    }

    const specs: BadgeSpec[] = [];

    // 노드 배지(🔗) — 바인딩 노드 rect(조각 분할이면 첫 조각) + 한글 요약.
    for (const { id, binding } of boundNodes) {
      const rect = getNodeRect(canvas, id);
      if (!rect) continue;
      const summary = buildBindingSummary(binding, catalog);
      specs.push({
        key: `badge-${id}`,
        title: summary.text,
        glyph: '🔗',
        bg: summary.legacy ? '#9ca3af' : '#2563eb',
        size: 16,
        rect,
      });
    }

    // 셀 배지 — DOM의 모든 [data-composer-cell]을 순회해 정의가 있는 좌표에 그린다(반복 복제 셀 전부).
    // 복제 셀은 정체성 좌표를 공유 → key 중복 방지용 등장 순번(#2, #3…) 접미.
    const uniq = makeUniqKey();
    for (const el of canvas.querySelectorAll<HTMLElement>('[data-composer-cell]')) {
      const coord = el.dataset.composerCell ?? '';
      const def = cellDefByCoord.get(coord);
      if (!def) continue;
      const { glyph, bg } = CELL_BADGE_STYLE[def.kind];
      specs.push({
        key: uniq(`cellbadge-${coord}`),
        title: buildCellDefTooltip(def, catalog),
        glyph,
        bg,
        size: 14,
        rect: rectRelativeTo(canvas, el),
      });
    }

    const sig = JSON.stringify(specs);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setBadges(specs);
    }
  }); // deps 없음 — 매 커밋(재측정 tick 포함) 후 최신 DOM 측정

  if (!canvas || badges.length === 0) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {badges.map(({ key, title, glyph, bg, size, rect }) => (
        <div
          key={key}
          title={title}
          // 기본 반투명 + hover 불투명 — 배지가 노드 우상단 콘텐츠를 상시 가리는 문제 완화(체인 바와 동일 패턴).
          className="opacity-40 hover:opacity-100 transition-opacity"
          style={badgeStyle(rect, bg, size)}
        >
          {glyph}
        </div>
      ))}
    </div>
  );
}

export const BindingBadgeOverlay = React.memo(BindingBadgeOverlayImpl);
